import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";

interface SystemStatus {
  pendingOutboxCount: number;
  runningJobsCount: number;
  failedJobsCount: number;
  needsOcrCount: number;
}

interface ProcessingJob {
  id: string;
  sourceId: string;
  step: string;
  status: "queued" | "running" | "succeeded" | "failed";
  attempts: number;
  errorMessage: string | null;
  createdAt: string;
}

const STATUS_COLORS: Record<ProcessingJob["status"], string> = {
  queued: "var(--pw-color-text-muted)",
  running: "var(--pw-color-accent-strong)",
  succeeded: "var(--pw-color-success)",
  failed: "var(--pw-color-danger)"
};

export function SystemStatusPage() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [jobs, setJobs] = useState<ProcessingJob[] | null>(null);

  const reload = useCallback(() => {
    void api.get<SystemStatus>("/api/v1/system/status").then(setStatus);
    void api.get<{ jobs: ProcessingJob[] }>("/api/v1/processing-jobs").then((r) => setJobs(r.jobs));
  }, []);

  useEffect(() => reload(), [reload]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <section className="pw-card">
        <h1 style={{ margin: "0 0 0.8rem", fontSize: "1.35rem", fontWeight: 600 }}>System</h1>
        <p style={{ margin: "0 0 1rem", fontSize: "0.82rem", color: "var(--pw-color-text-muted)" }}>
          Verarbeitungspipeline (Outbox → Queue → Workflow). Keine Quellinhalte, nur Status.
        </p>
        {status && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.7rem" }}>
            {[
              { label: "Offene Outbox-Ereignisse", value: status.pendingOutboxCount },
              { label: "Laufende Jobs", value: status.runningJobsCount },
              { label: "Fehlgeschlagene Jobs", value: status.failedJobsCount },
              { label: "Quellen mit needs_ocr", value: status.needsOcrCount }
            ].map((s) => (
              <div key={s.label} style={{ padding: "0.8rem", background: "var(--pw-color-surface-flat)", border: "1px solid var(--pw-color-surface-flat-border)", borderRadius: "var(--pw-radius-sm)" }}>
                <p style={{ margin: "0 0 0.2rem", fontSize: "1.4rem", fontWeight: 700 }}>{s.value}</p>
                <p style={{ margin: 0, fontSize: "0.72rem", color: "var(--pw-color-text-muted)" }}>{s.label}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="pw-card">
        <p style={{ margin: "0 0 0.7rem", fontSize: "11px", fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "var(--pw-color-text-muted)" }}>
          Letzte Verarbeitungsjobs
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          {jobs?.map((j) => (
            <div key={j.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.5rem 0", borderBottom: "1px solid var(--pw-color-surface-flat-border)", fontSize: "0.82rem" }}>
              <span style={{ color: STATUS_COLORS[j.status], fontWeight: 600, minWidth: "80px" }}>{j.status}</span>
              <span style={{ flex: 1, minWidth: 0, fontFamily: "var(--pw-font-mono)", fontSize: "0.75rem", color: "var(--pw-color-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {j.sourceId}
              </span>
              <span style={{ color: "var(--pw-color-text-subtle)" }}>{j.step}</span>
              <span style={{ color: "var(--pw-color-text-subtle)" }}>{j.attempts}×</span>
              {j.errorMessage && (
                <span style={{ color: "var(--pw-color-danger)", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={j.errorMessage}>
                  {j.errorMessage}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
